// src/data/mock/successStories.ts

/**
 * Success Stories Data Structure
 * 
 * Stories of athletes who started at Champions Academy and went on
 * to achieve great success in their sporting careers.
 * 
 * @note Replace placeholder data with actual success stories
 * @note All images should be placed in /public/images/success-stories/ directory
 */

export interface SuccessStory {
  id: string;
  
  // Basic Information
  name: {
    en: string;
    ar: string;
  };
  
  // Sport they excelled in
  sport: {
    en: string;
    ar: string;
  };
  
  // Main image (athlete photo)
  image: string;
  
  // Additional images (gallery)
  gallery?: string[];
  
  // Current club/team they play for
  currentClub?: {
    en: string;
    ar: string;
  };
  
  // Club logo
  clubLogo?: string;
  
  // Current position/role
  position?: {
    en: string;
    ar: string;
  };
  
  // Years at Champions Academy
  yearsAtAcademy: string; // e.g., "2015 - 2019"
  
  // Current age
  age?: number;
  
  // Nationality
  nationality?: {
    en: string;
    ar: string;
  };
  
  // Jersey number
  jerseyNumber?: number;
  
  // Career level
  level: {
    en: 'Professional' | 'Semi-Professional' | 'National Team' | 'International';
    ar: 'محترف' | 'شبه محترف' | 'منتخب وطني' | 'دولي';
  };
  
  // Success story narrative
  story: {
    en: string;
    ar: string;
  };
  
  // Quote from the athlete
  quote?: {
    en: string;
    ar: string;
  };
  
  // Key achievements
  achievements?: {
    en: string[];
    ar: string[];
  };
  
  // Statistics (for display)
  stats?: {
    label: { en: string; ar: string };
    value: string | number;
    icon?: string;
  }[];
  
  // Career timeline
  timeline?: {
    year: string;
    event: {
      en: string;
      ar: string;
    };
    highlight?: boolean;
  }[];
  
  // Social media links
  socialMedia?: {
    instagram?: string;
    twitter?: string;
    facebook?: string;
    youtube?: string;
  };
  
  // Featured story (show prominently)
  featured?: boolean;
  
  // Video URL (YouTube, Vimeo, etc.)
  videoUrl?: string;
  
  // Tags for filtering
  tags?: string[];
}

/**
 * Success Stories Data
 *
 * Real success stories of Champions Academy athletes (Gaza),
 * translated professionally from the academy's source document.
 * Football and basketball players who grew through the academy.
 *
 * @note Photos use generic academy imagery as placeholders until
 *       real athlete portraits are provided.
 */
export const successStories: SuccessStory[] = [
  // ==========================================
  // FEATURED SUCCESS STORY - قصة مميزة
  // ==========================================
  {
    id: "ss-football-shallah",
    featured: true,
    name: {
      en: "Ahmed Abdel-Rahim Al-Shallah",
      ar: "أحمد عبد الرحيم الشلح"
    },
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    image: "/assets/images/s-blob-v1-IMAGE-56VzkL9pHSw.jpeg",
    gallery: [
      "/assets/images/s-blob-v1-IMAGE-9MjiX0xpxqY.jpeg",
      "/assets/images/s-blob-v1-IMAGE-gwHF0h6PBSA.jpeg"
    ],
    currentClub: {
      en: "Al-Bireh Club (formerly Al-Jalaa)",
      ar: "نادي البيرة (سابقاً الجلاء)"
    },
    position: {
      en: "Midfielder",
      ar: "لاعب وسط"
    },
    yearsAtAcademy: "2019 - 2023",
    age: 20,
    nationality: {
      en: "Palestinian",
      ar: "فلسطيني"
    },
    level: {
      en: "National Team",
      ar: "منتخب وطني"
    },
    story: {
      en: "From a young age, Ahmed Abdel-Rahim Al-Shallah — a son of Gaza with roots in Rafah — began his football journey with great determination and unstoppable ambition. Now 20 years old and playing in midfield, Ahmed joined Champions Academy in 2019, where his talent began to take shape and flourish under an elite group of coaches, among them Captain Aboud Abu Daher, Captain Mohammed Al-Najjar, Captain Ismail Madhoun, and Captain Ahmed Al-Daour.\n\nThe road was not easy from the start. Shortly before an important tournament, Ahmed suffered a serious injury — a fracture of the shin bone — and underwent surgery that nearly kept him off the pitch and out of the 2019 Clubs and Academies Seven-a-Side Championship. But with a fighter's spirit, the support of his coaches, and the follow-up care of Dr. Abd Talib throughout his rehabilitation, Ahmed returned to the field stronger than before, joining his team and helping them win the 2019 Clubs and Academies Seven-a-Side Championship.\n\nAhmed's ambition did not stop there. In 2020 he competed with Champions Academy in the 2006–2007 age-group championship, led by Captain Aboud Abu Daher and Captain Mohammed Al-Najjar. The team reached the semi-finals, and Ahmed left his mark on the pitch by contributing to 9 goals, proving himself a player with vision, the ability to make a difference, and a strong presence in the heart of midfield.\n\nAfter this standout performance, Ahmed was called up to the youth national team camp, where he trained for six months and was named among its outstanding players — although passport-related complications prevented him from travelling with the team at that stage.\n\nIn 2021, Ahmed returned to the same tournament in the same age group, under Captain Ahmed Al-Daour, Captain Mohammed Al-Najjar, and Captain Ismail Madhoun. He continued to shine, contributing to 6 goals, and was selected once again for the youth national team. This time the dream came true: he represented the national team at the Arab Cup in Algeria — a milestone any young player would be proud of.\n\nHe then moved to Al-Jalaa Club at just 16 years old and joined its first team — a major step reflecting the trust placed in his talent and his ability to play at a higher level despite his age. A year later he moved to the northern governorates and spent a trial period with Al-Bireh Club, a new experience full of ambition and challenge.\n\nBut as with many athletes in Palestine, the war and its harsh conditions brought sporting activity to a halt and placed the players' dreams before great challenges. Even so, Ahmed's story stands as testament that true talent is measured not only by titles, but by determination, by coming back after injury, and by the ability to carry on despite the circumstances.\n\nAnd although sport has stopped because of the war, Ahmed still believes his comeback is coming — that what he built over the past years is not the end of the road, but the beginning of a bigger dream awaiting a fresh chance to take off.",
      ar: "في عمرٍ مبكر، بدأ اللاعب أحمد عبد الرحيم الشلح، ابن غزة وأصله من رفح، رحلته الكروية بإصرار كبير وطموح لا يتوقف. أحمد، الذي يبلغ من العمر 20 عاماً ويلعب في مركز نصف الملعب، انضم إلى أكاديمية تشامبيونز عام 2019، وهناك بدأت ملامح موهبته تظهر وتتطور تحت إشراف نخبة من المدربين، منهم الكابتن عبود أبو ضاهر، والكابتن محمد النجار، والكابتن إسماعيل مدهون، والكابتن أحمد الداعور.\n\nلم تكن بداية الطريق سهلة، فقد تعرّض أحمد قبل إحدى البطولات المهمة لإصابة صعبة تمثلت في كسر في عظمة الساق، وخضع على إثرها لعملية جراحية كادت أن تبعده عن الملاعب وعن المشاركة في بطولة السباعي للأندية والأكاديميات عام 2019. لكن بروح اللاعب المقاتل، وبدعم المدربين، ومتابعة الدكتور عبد طالب في مرحلة التأهيل، استطاع أحمد أن يعود إلى الملعب أقوى من قبل، ليشارك مع فريقه ويساهم في تحقيق بطولة السباعي للأندية والأكاديميات عام 2019.\n\nلم يتوقف طموح أحمد عند هذه المحطة، ففي عام 2020 شارك مع أكاديمية تشامبيونز في بطولة مواليد 2006–2007، تحت قيادة الكابتن عبود أبو ضاهر والكابتن محمد النجار. وصل الفريق إلى نصف النهائي، وكان لأحمد حضور مؤثر داخل الملعب، حيث ساهم في 9 أهداف، ليؤكد أنه لاعب يمتلك رؤية، وقدرة على صناعة الفارق، وشخصية قوية في وسط الميدان.\n\nوبعد هذا الأداء المميز، تم اختيار أحمد للانضمام إلى تجمع منتخب الناشئين، حيث استمر في التدريبات لمدة ستة أشهر، وتم اختياره ضمن العناصر المميزة، إلا أن بعض المشاكل المتعلقة بجواز السفر حالت دون سفره مع المنتخب في تلك المرحلة.\n\nفي عام 2021، عاد أحمد للمشاركة في نفس البطولة للفئة العمرية ذاتها، تحت قيادة الكابتن أحمد الداعور، والكابتن محمد النجار، والكابتن إسماعيل مدهون. واصل تألقه وساهم في 6 أهداف، ليتم اختياره مرة أخرى لمنتخب الناشئين، وهذه المرة تحقق الحلم، حيث مثّل المنتخب في بطولة كأس العرب في الجزائر، وهي محطة مهمة يفتخر بها أي لاعب شاب.\n\nبعد ذلك، انتقل أحمد إلى نادي الجلاء وهو في عمر 16 عاماً فقط، وانضم إلى الفريق الأول، وهي خطوة كبيرة تعكس حجم الثقة بموهبته وقدرته على اللعب في مستويات أعلى رغم صغر سنه. وبعد عام، انتقل إلى المحافظات الشمالية، وخاض فترة معايشة مع نادي البيرة، في تجربة جديدة كانت تحمل الكثير من الطموح والتحدي.\n\nلكن كما هو حال كثير من الرياضيين في فلسطين، جاءت الحرب وظروفها القاسية لتوقف النشاط الرياضي، وتضع أحلام اللاعبين أمام تحديات كبيرة. ومع ذلك، بقيت قصة أحمد شاهداً على أن الموهبة الحقيقية لا تُقاس فقط بالألقاب، بل بالإصرار، والعودة بعد الإصابة، والقدرة على الاستمرار رغم الظروف.\n\nورغم توقف الرياضة بسبب الحرب، ما زال أحمد يؤمن أن العودة قادمة، وأن ما بناه خلال السنوات الماضية ليس نهاية الطريق، بل بداية لحلم أكبر ينتظر فرصة جديدة للانطلاق."
    },
    achievements: {
      en: [
        "Champion of the 2019 seven-a-side tournament",
        "Scored 9 goals in the 2006-2007 age-group championship",
        "Contributed 6 goals in the 2021 season",
        "Selected for the Palestinian youth national team",
        "Represented Palestine at the Arab Cup in Algeria",
        "Signed with Al-Jalaa Club at age 16, later Al-Bireh"
      ],
      ar: [
        "بطل بطولة السباعي 2019",
        "سجّل 9 أهداف في بطولة مواليد 2006-2007",
        "ساهم بـ 6 أهداف في موسم 2021",
        "اختير لمنتخب فلسطين للناشئين",
        "مثّل فلسطين في كأس العرب بالجزائر",
        "وقّع لنادي الجلاء في سن 16 ثم نادي البيرة"
      ]
    },
    stats: [
      {
        label: { en: "Goals", ar: "أهداف" },
        value: "15+",
        icon: "⚽"
      },
      {
        label: { en: "Titles", ar: "بطولات" },
        value: "1",
        icon: "🏆"
      }
    ],
    timeline: [
      {
        year: "2019",
        event: {
          en: "Joined Champions Academy and won the seven-a-side title",
          ar: "انضم لأكاديمية تشامبيونز وفاز ببطولة السباعي"
        },
        highlight: true
      },
      {
        year: "2021",
        event: {
          en: "Called up to the Palestinian youth national team",
          ar: "استُدعي لمنتخب فلسطين للناشئين"
        },
        highlight: true
      },
      {
        year: "2022",
        event: {
          en: "Represented Palestine at the Arab Cup in Algeria",
          ar: "مثّل فلسطين في كأس العرب بالجزائر"
        },
        highlight: true
      }
    ],
    tags: ["football", "national-team", "rafah", "midfielder"]
  },

  // ==========================================
  // FOOTBALL PLAYERS - لاعبو كرة القدم
  // ==========================================
  {
    id: "ss-football-sharab",
    name: {
      en: "Mohammed Alaa Haidar Sharab",
      ar: "محمد علاء حيدر شراب"
    },
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    image: "/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg",
    position: {
      en: "Central Midfielder",
      ar: "وسط ملعب محوري"
    },
    yearsAtAcademy: "2018 - Present",
    age: 17,
    nationality: {
      en: "Palestinian",
      ar: "فلسطيني"
    },
    level: {
      en: "Semi-Professional",
      ar: "شبه محترف"
    },
    story: {
      en: "In the heart of Gaza, specifically in the Tal Al-Hawa area – Roundabout 17, the story of a young player began — one who carried the dream of football from childhood and pursued it with relentless determination to carve out a path for himself on the green rectangle. This is Mohammed Alaa Haidar Sharab, born on 09/11/2008, aged 17, one of the sons of Champions Academy who left a clear mark on their sporting journey.\n\nMohammed began his football journey with Champions Academy in July 2018. From day one, football was never a mere hobby to him — it was a dream and an ambition he sought to achieve step by step. He saw in the academy the place where he could develop his talent, build his sporting character, and reach a level worthy of his dreams.\n\nAt the start of his journey, Mohammed trained at the academy's branch in the Industrial area. As time passed, the academy grew to become one of the largest sports academies in Palestine, and he too kept working and striving to develop his technical and physical level. The road was not easy, but he believed that commitment to training, respect for the coaches' instructions, and patience through challenges are the true keys to success.\n\nThroughout his journey, a group of coaches oversaw his training and played a major role in honing his talent and developing his level, among them Captain Youssef Al-Hila, Captain Mohammed Al-Najjar, Captain Ismail Al-Madhoun, Captain Ahmed Al-Daour, Captain Abd Al-Aal Al-Attar, Captain Aboud, and Captain Mohammed Al-Ashram. Each of them left an important impact on Mohammed's character as a player — technically, physically, and mentally.\n\nMohammed took part in many tournaments with Champions Academy and achieved several important accomplishments, most notably first place and gold medals in more than one tournament. Yet the Gaza Sector Championship in 2023 remains the most prominent and important milestone of his sporting career so far.\n\nBefore the 2023 Gaza Sector Championship kicked off, Mohammed faced a difficult challenge after being away from football for nearly two months. On his return, he was not at his best technically or physically and needed great effort to regain his true level. Here the role of the technical staff stood out, working intensively with him to prepare him and bring him back into competitive shape.\n\nDuring the preparation period, Mohammed trained at some of the most important stadiums in the Gaza Strip, including Al-Yarmouk Stadium, the Martyr Mohammed Al-Durra Stadium, Khan Younis Union Stadium, and Al-Maghazi Stadium. This was one of the hardest and most important stages of preparation, requiring discipline, endurance, and determination in order to come back strong.\n\nThe technical staff led the team in that tournament under Captain Youssef Al-Hila, assisted by Captain Ismail Al-Madhoun, Captain Abd Al-Aal Al-Attar, and Captain Mohammed Al-Najjar. Thanks to teamwork, good preparation, and the players' spirit, Champions Academy was able to deliver a strong and distinguished campaign.\n\nDuring the tournament, Mohammed had a clear presence on the pitch, helping to create many attacking chances and scoring an important goal against Al-Tuffah Club — a goal that helped boost the team's confidence and contributed to reaching the final.\n\nIn the final, Champions Academy faced Khan Younis Union before a large crowd and an atmosphere full of excitement and pressure. Despite the difficulty of the moment, Mohammed delivered a distinguished performance worthy of the academy's name, reflecting his character as a player able to shoulder responsibility in the important moments.\n\nThe tournament was not without its difficulties: Mohammed suffered an injury that almost prevented him from completing the competition, but his resolve was stronger than the pain. By God's grace, and with the support of the technical and medical staff, he was able to return and take part until the end of the tournament, becoming part of the great achievement.\n\nAt the close of the tournament, Champions Academy was crowned champion of the Gaza Sector 2023, and this coronation was a historic moment in Mohammed's career. The title was not merely a medal or a cup; it was the result of years of training, fatigue, determination, and the challenges the player had faced since the beginning of his journey.\n\nToday, Mohammed Alaa Haidar Sharab continues his dream with greater confidence, and his ambition does not stop at this achievement. He seeks to keep developing his technical and physical level, to win more titles, to represent the Palestine national team for those born in 2008, and to reach higher levels in local and international football.\n\nMohammed's story is the story of a young player who believed in his talent, worked hard for his dream, and overcame challenges with determination and effort. It is a message to every aspiring young player that success does not come easily — it requires patience, training, discipline, and support from people who believe in your abilities.\n\nAnd despite the difficult circumstances facing sport in Palestine, Mohammed remains a model of the ambitious Palestinian player who does not let challenges stop his dream, but turns them into motivation to carry on and succeed.",
      ar: "في قلب غزة، وتحديدًا من منطقة تل الهوى – دوار 17، بدأت حكاية لاعب شاب حمل حلم كرة القدم منذ طفولته، وسعى بكل إصرار ليصنع لنفسه طريقًا داخل المستطيل الأخضر. إنه اللاعب محمد علاء حيدر شراب، من مواليد 09/11/2008، والذي يبلغ من العمر 17 عامًا، وأحد أبناء أكاديمية تشامبيونز الذين تركوا بصمة واضحة في مسيرتهم الرياضية.\n\nبدأ محمد رحلته الكروية مع أكاديمية تشامبيونز في شهر يوليو عام 2018. ومنذ اليوم الأول، لم تكن كرة القدم بالنسبة له مجرد هواية، بل كانت حلمًا وطموحًا يسعى لتحقيقه خطوة بخطوة. رأى في الأكاديمية المكان الذي يستطيع من خلاله تطوير موهبته، وبناء شخصيته الرياضية، والوصول إلى مستوى يليق بأحلامه.\n\nفي بداية مشواره، تدرب محمد في فرع الأكاديمية بمنطقة الصناعة، ومع مرور الوقت تطورت الأكاديمية وأصبحت من أكبر الأكاديميات الرياضية في فلسطين، وواصل هو بدوره العمل والاجتهاد لتطوير مستواه الفني والبدني. لم يكن الطريق سهلًا، لكنه كان يؤمن أن الالتزام بالتدريب، واحترام تعليمات المدربين، والصبر على التحديات هي مفاتيح النجاح الحقيقي.\n\nخلال مسيرته، أشرف على تدريبه مجموعة من المدربين الذين كان لهم دور كبير في صقل موهبته وتطوير مستواه، ومنهم الكابتن يوسف الحيلة، والكابتن محمد النجار، والكابتن إسماعيل المدهون، والكابتن أحمد الدعور، والكابتن عبد العال العطار، والكابتن عبود، والكابتن محمد الأشرم. وقد ترك كل مدرب منهم أثرًا مهمًا في شخصية محمد كلاعب، سواء من الناحية الفنية أو البدنية أو النفسية.\n\nشارك محمد في العديد من البطولات مع أكاديمية تشامبيونز، واستطاع أن يحقق عدة إنجازات مهمة، من أبرزها الفوز بالمركز الأول والحصول على الميداليات الذهبية في أكثر من بطولة. لكن تبقى بطولة قطاع غزة عام 2023 المحطة الأبرز والأهم في مسيرته الرياضية حتى الآن.\n\nقبل انطلاق بطولة قطاع غزة 2023، واجه محمد تحديًا صعبًا بعدما ابتعد عن كرة القدم لمدة تقارب الشهرين. وعند عودته، لم يكن في أفضل حالاته الفنية والبدنية، وكان يحتاج إلى جهد كبير ليستعيد مستواه الحقيقي. هنا ظهر دور الجهاز الفني، الذي عمل معه بشكل مكثف من أجل تجهيزه وإعادته إلى أجواء المنافسة.\n\nتدرب محمد خلال فترة الإعداد في عدد من أهم ملاعب قطاع غزة، منها ملعب اليرموك، وملعب الشهيد محمد الدرة، وملعب اتحاد خانيونس، وملعب المغازي. وكانت هذه المرحلة من أصعب وأهم مراحل التحضير، حيث احتاج اللاعب إلى الانضباط، والتحمل، والإصرار من أجل العودة بقوة.\n\nقاد الجهاز الفني الفريق في تلك البطولة بقيادة الكابتن يوسف الحيلة، وبمساعدة الكابتن إسماعيل المدهون، والكابتن عبد العال العطار، والكابتن محمد النجار. وبفضل العمل الجماعي، والتحضير الجيد، وروح اللاعبين، استطاعت أكاديمية تشامبيونز أن تقدم بطولة قوية ومميزة.\n\nوخلال البطولة، كان لمحمد حضور واضح داخل الملعب، حيث ساهم في صناعة العديد من الفرص الهجومية، ونجح في تسجيل هدف مهم أمام نادي التفاح، وهو هدف كان له دور في تعزيز ثقة الفريق والمساهمة في الوصول إلى المباراة النهائية.\n\nوفي النهائي، واجهت أكاديمية تشامبيونز فريق اتحاد خانيونس وسط حضور جماهيري كبير وأجواء مليئة بالحماس والضغط. ورغم صعوبة الموقف، استطاع محمد أن يقدم مستوى مميزًا يليق باسم الأكاديمية، ويعكس شخصيته كلاعب قادر على تحمل المسؤولية في اللحظات المهمة.\n\nلم تخلُ البطولة من الصعوبات، فقد تعرض محمد لإصابة كادت أن تمنعه من إكمال المنافسات، إلا أن عزيمته كانت أقوى من الألم. وبفضل الله، ثم بدعم الجهاز الفني والطبي، تمكن من العودة والمشاركة حتى نهاية البطولة، ليكون جزءًا من الإنجاز الكبير.\n\nوفي ختام البطولة، تُوجت أكاديمية تشامبيونز بلقب بطل قطاع غزة 2023، وكان هذا التتويج لحظة تاريخية في مسيرة محمد. لم يكن اللقب مجرد ميدالية أو كأس، بل كان نتيجة سنوات من التدريب، والتعب، والإصرار، والتحديات التي واجهها اللاعب منذ بداية رحلته.\n\nاليوم، يواصل محمد علاء حيدر شراب حلمه بثقة أكبر، وطموحه لا يتوقف عند هذا الإنجاز. فهو يسعى إلى مواصلة تطوير مستواه الفني والبدني، وتحقيق المزيد من البطولات، وتمثيل منتخب فلسطين لمواليد 2008، والوصول إلى مستويات أعلى في كرة القدم المحلية والدولية.\n\nقصة محمد هي قصة لاعب شاب آمن بموهبته، واجتهد من أجل حلمه، وتجاوز التحديات بالإصرار والعمل. وهي رسالة لكل لاعب ناشئ أن النجاح لا يأتي بسهولة، بل يحتاج إلى صبر، وتدريب، وانضباط، ودعم من أشخاص يؤمنون بقدراتك.\n\nورغم الظروف الصعبة التي تمر بها الرياضة في فلسطين، يبقى محمد نموذجًا للاعب الفلسطيني الطموح، الذي لا يسمح للتحديات أن توقف حلمه، بل يحولها إلى دافع للاستمرار والنجاح."
    },
    achievements: {
      en: [
        "Champion of the 2023 Gaza Sector Championship",
        "Led his team to the title while playing through injury",
        "Competed in multiple academy tournaments"
      ],
      ar: [
        "بطل بطولة قطاع غزة 2023",
        "قاد فريقه للقب رغم اللعب بإصابة",
        "شارك في العديد من بطولات الأكاديمية"
      ]
    },
    tags: ["football", "gaza", "midfielder", "youth"]
  },

  {
    id: "ss-football-alyan",
    name: {
      en: "Abdullah Wissam Radwan Alyan",
      ar: "عبدالله وسام رضوان عليان"
    },
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    image: "/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg",
    currentClub: {
      en: "Al-Jalaa Club",
      ar: "نادي الجلاء"
    },
    position: {
      en: "Goalkeeper",
      ar: "حارس مرمى"
    },
    yearsAtAcademy: "2015 - 2023",
    age: 20,
    nationality: {
      en: "Palestinian",
      ar: "فلسطيني"
    },
    level: {
      en: "National Team",
      ar: "منتخب وطني"
    },
    story: {
      en: "The goalkeeper who kept his dream alive despite loss and war.\n\nI am the player Abdullah Wissam Radwan Alyan, 20 years old, a resident of the central area – Al-Nuseirat in the Gaza Strip. I began my journey with football when I was 10 years old, and my start was like that of many Palestinian players in Gaza — from the alleys and the simple streets, where the passion grew day after day.\n\nI chose the goalkeeper position, a position that requires courage, focus, strength of character, and the ability to handle pressure. From here my real journey with football began, through Champions Academy, which was a very important station in building my sporting character and developing my talent.\n\nIn my early days, I trained at the central branch under Captain Hani Al-Musaddar and Captain Youssef Al-Hila — may they rest in peace — and Captain Ahmed Shaltout. Over time, I moved to the Gaza branch, where I trained under a number of coaches who played a major role in developing my level, among them Captain Ahmed Al-Daour, Captain Abd Al-Aal Al-Attar, Captain Aboud Abu Daher, and Captain Mohammed Al-Najjar. I also received specialized goalkeeper training with Captain Jawda Okasha and Captain Ismail Al-Madhoun.\n\nThroughout my career I took part in several important tournaments, most notably the Donosti Cup in Spain in 2019 with the Champions 2006 team. It was a very special experience because it gave me the chance to compete against teams and players from outside Palestine, and it opened up a wider horizon for me to dream and turn professional.\n\nI also took part in three tournaments organized by the Palestinian Football Federation, including the 2020 Academies Championship, in which we finished first — one of the most beautiful moments of my career. In 2021 I took part in the Palestinian Federation Clubs Championship, and we reached the semi-finals of the Gaza and North region, playing against Al-Wehda Academy, but luck was not on our side. In 2023 I took part in another Federation tournament, and we again reached the semi-finals, playing against Namaa Club, but we did not manage to reach the final.\n\nAfter these appearances came a very important milestone in my life: I was selected for the Palestine national team, and travelled with the team to take part in the West Asia Cup in the city of Dammam, Saudi Arabia. This selection was a huge motivation for me and an indescribable feeling, because it confirmed to me that effort and hard work are never wasted.\n\nBut my path was not easy. At the beginning of 2022 I lost my father — may he rest in peace — who was the first supporter in my life. His passing affected me greatly, psychologically and emotionally, and in that period I felt a heavy weight, but I did not let grief stop my dream. I decided to carry on, and to work even harder to achieve my father's dream: that I reach the professional stage outside Gaza.\n\nRecently, I joined Al-Jalaa Sports Club and trained under Captain Aboud Abu Daher and Captain Ismail Al-Madhoun, with goalkeeper training under Captain Mahmoud Afaneh — may they rest in peace. I played three official matches with the team in the Gaza First Division League, and this step was an important milestone in my journey as a player seeking to prove himself at club level.\n\nThen came the war, and it changed everything. Football stopped, the pitches fell silent, and we lost many loved ones. The war did not only destroy places; it affected us psychologically and emotionally and took from us our passion and our peace of mind. There was no longer training as there had been, no matches, and no sporting atmosphere to help a player develop.\n\nDespite all of this, I still believe that the dream does not end. Circumstances may stop, and pitches may disappear, but determination remains within a person. My story is not only the story of a footballer, but the story of a young Palestinian from Gaza who started in the alleys, faced loss, lived through war, yet still holds on to his dream.",
      ar: "حارس المرمى الذي واصل حلمه رغم الفقد والحرب.\n\nأنا اللاعب عبدالله وسام رضوان عليان، أبلغ من العمر 20 عاماً، من سكان منطقة الوسطى – النصيرات في قطاع غزة. بدأت رحلتي مع كرة القدم منذ أن كان عمري 10 سنوات، وكانت بدايتي مثل بداية كثير من اللاعبين الفلسطينيين في غزة؛ من الحواري والشوارع البسيطة، حيث بدأ الشغف يكبر يوماً بعد يوم.\n\nاخترت مركز حارس المرمى، وهو مركز يحتاج إلى شجاعة، وتركيز، وقوة شخصية، وقدرة على تحمل الضغط. ومن هنا بدأت رحلتي الحقيقية مع كرة القدم من خلال أكاديمية تشامبيونز، التي كانت محطة مهمة جداً في بناء شخصيتي الرياضية وتطوير موهبتي.\n\nفي بداياتي، تدربت في فرع الوسطى تحت إشراف الكابتن هاني المصدر والكابتن يوسف الحيلة رحمهم الله، والكابتن أحمد شلتوت. ومع مرور الوقت، انتقلت إلى فرع غزة، وهناك تدربت على يد عدد من المدربين الذين كان لهم دور كبير في تطوير مستواي، منهم الكابتن أحمد الدعور، والكابتن عبد العال العطار، والكابتن عبود أبو ضاهر، والكابتن محمد النجار. كما تلقيت تدريبات خاصة لحراس المرمى مع الكابتن جودة عكاشة والكابتن إسماعيل المدهون.\n\nشاركت خلال مسيرتي في عدة بطولات مهمة، من أبرزها بطولة دونيستي كاب في إسبانيا عام 2019 مع فريق تشامبيونز 2006، وكانت تجربة مميزة جداً لأنها منحتني فرصة الاحتكاك بفرق ولاعبين من خارج فلسطين، وفتحت أمامي أفقاً أكبر للحلم والاحتراف.\n\nكما شاركت في ثلاث بطولات تابعة للاتحاد الفلسطيني، منها بطولة الأكاديميات عام 2020 التي حققنا فيها المركز الأول، وكانت من أجمل لحظات مسيرتي الرياضية. وفي عام 2021 شاركت في بطولة الاتحاد الفلسطيني للأندية، ووصلنا إلى نصف نهائي فرع غزة والشمال، ولعبنا ضد أكاديمية الوحدة، لكن لم يحالفنا التوفيق. وفي عام 2023 شاركت في بطولة أخرى تابعة للاتحاد، ووصلنا أيضاً إلى نصف النهائي ولعبنا ضد نادي نماء، ولم نوفق في الوصول للنهائي.\n\nبعد هذه المشاركات، جاءت محطة مهمة جداً في حياتي، حيث تم اختياري ضمن منتخب فلسطين، وسافرت مع المنتخب للمشاركة في بطولة غرب آسيا في مدينة الدمام بالمملكة العربية السعودية. كان هذا الاختيار دافعاً كبيراً لي، وشعوراً لا يوصف، لأنه أكد لي أن التعب والاجتهاد لا يضيعان.\n\nلكن طريقي لم يكن سهلاً. في بداية عام 2022 فقدت والدي، رحمه الله، وكان هو الداعم الأول في حياتي. أثرت وفاته عليّ كثيراً من الناحية النفسية والمعنوية، وشعرت في تلك المرحلة بثقل كبير، لكنني لم أسمح للحزن أن يوقف حلمي. قررت أن أواصل الطريق، وأن أجتهد أكثر من أجل تحقيق حلم والدي، وهو أن أصل إلى مرحلة الاحتراف خارج غزة.\n\nمؤخراً، انضممت إلى نادي الجلاء الرياضي، وتدربت تحت إشراف الكابتن عبود أبو ضاهر والكابتن إسماعيل المدهون، وفي تدريبات الحراس مع الكابتن محمود عفانة رحمهم الله. وشاركت مع الفريق في ثلاث مباريات رسمية في دوري غزة للدرجة الأولى، وكانت هذه الخطوة محطة مهمة في مسيرتي كلاعب يسعى لإثبات نفسه على مستوى الأندية.\n\nثم جاءت الحرب، فغيّرت كل شيء. توقفت كرة القدم، وغابت الملاعب، وفقدنا الكثير من الأحباب. الحرب لم تدمر الأماكن فقط، بل أثرت علينا نفسياً ومعنوياً، وأخذت منّا الشغف والطمأنينة. لم يعد هناك تدريب كما كان، ولا مباريات، ولا أجواء رياضية تساعد اللاعب على التطور.\n\nورغم كل ذلك، ما زلت أؤمن أن الحلم لا ينتهي. قد تتوقف الظروف، وقد تغيب الملاعب، لكن الإصرار يبقى داخل الإنسان. قصتي ليست فقط قصة لاعب كرة قدم، بل قصة شاب فلسطيني من غزة بدأ من الحواري، وواجه الفقد، ومرّ بالحرب، لكنه ما زال متمسكاً بحلمه."
    },
    quote: {
      en: "Don't let circumstances stop you. You may lose a match, fall a step behind, or go through harsh conditions — but as long as you have a dream and faith in yourself, you will always be able to come back again.",
      ar: "لا تجعل الظروف توقفك. قد تخسر مباراة، وقد تتأخر خطوة، وقد تمر بظروف قاسية، لكن طالما لديك حلم وإيمان بنفسك، ستبقى قادراً على العودة من جديد."
    },
    achievements: {
      en: [
        "Selected for the Palestinian national team",
        "Competed in the West Asia Cup",
        "Played at the 2019 Donosti Cup in Spain",
        "Competed in Palestinian Football Federation tournaments"
      ],
      ar: [
        "اختير لمنتخب فلسطين",
        "شارك في بطولة غرب آسيا",
        "لعب في بطولة دونوستي كاب بإسبانيا 2019",
        "شارك في بطولات الاتحاد الفلسطيني لكرة القدم"
      ]
    },
    tags: ["football", "national-team", "goalkeeper", "nuseirat"]
  },

  {
    id: "ss-football-mozher",
    name: {
      en: "Wisam Hossam Mohammad Mozher",
      ar: "وسام حسام محمد مزهر"
    },
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    image: "/assets/images/s-blob-v1-IMAGE-fCIF9jo-oiw.jpeg",
    yearsAtAcademy: "2018 - Present",
    age: 15,
    nationality: {
      en: "Palestinian",
      ar: "فلسطيني"
    },
    level: {
      en: "Semi-Professional",
      ar: "شبه محترف"
    },
    story: {
      en: "I am the player Wisam Hossam Mohammad Mozher, born on 29/07/2008, and I live in Camp 5 in Al-Nuseirat. I began my football journey at a young age, and my start was like that of many Gaza players — from a love of the ball and playing in the alleys — before moving on to organized training inside Champions Academy.\n\nFrom the moment I joined the academy, I began learning the fundamentals of football and developing my skills step by step. The academy was an important station in my sporting life, because it gave me the chance to train, to be disciplined, and to take part in tournaments, and it helped me build my character as a player both on and off the pitch.\n\nThroughout my journey I was trained by a number of coaches who played a major role in developing my level, among them: Captain Youssef Al-Hila, Captain Abd Al-Aal Al-Attar, Captain Mohammed Al-Najjar, Captain Ahmed Al-Daour, Captain Aboud Abu Daher, and Captain Ismail Al-Madhoun.\n\nI took part with Champions Academy in a number of tournaments, most notably the 2006 and 2008 age-group championships. In 2023 I took part in a tournament for those born in 2008, and we achieved a distinguished accomplishment by claiming first place at the Strip level, after winning the final against Deir Al-Balah United.\n\nThis achievement was one of the most important milestones in my sporting career, because it proved to me that effort, hard work, and discipline in training can make a big difference. Despite the difficult circumstances the Gaza Strip is going through, my dream in football has remained present, and I still aspire to develop myself further, reach higher levels, and represent my academy and my city in the best possible way.\n\nIn conclusion, I consider my experience with Champions Academy to have been a true beginning for my path in football, and I hope to keep working and striving until I achieve my dream and become a successful player who raises the name of Palestine high.",
      ar: "أنا اللاعب وسام حسام محمد مزهر، من مواليد 29/07/2008، وأسكن في مخيم 5 بالنصيرات. بدأت رحلتي في كرة القدم منذ الصغر، وكانت بدايتي مثل كثير من لاعبي غزة، من حب الكرة واللعب في الحارات، ثم انتقلت إلى التدريب بشكل منظم داخل أكاديمية تشامبيونز.\n\nمنذ انضمامي للأكاديمية، بدأت أتعلم أساسيات كرة القدم وأطور مهاراتي خطوة بخطوة. كانت الأكاديمية محطة مهمة في حياتي الرياضية، لأنها منحتني فرصة للتدريب والانضباط والمشاركة في البطولات، وساعدتني على بناء شخصيتي كلاعب داخل الملعب وخارجه.\n\nتدربت خلال مسيرتي على يد عدد من المدربين الذين كان لهم دور كبير في تطوير مستواي، ومنهم: الكابتن يوسف الحيلة، والكابتن عبد العال العطار، والكابتن محمد النجار، والكابتن أحمد الداعور، والكابتن عبود أبو ظاهر، والكابتن إسماعيل المدهون.\n\nشاركت مع أكاديمية تشامبيونز في عدد من البطولات، ومن أبرزها بطولات مواليد 2006 و2008. وفي عام 2023 شاركت في بطولة لمواليد 2008، واستطعنا تحقيق إنجاز مميز بالحصول على المركز الأول على مستوى القطاع، بعد الفوز في المباراة النهائية على اتحاد دير البلح.\n\nكان هذا الإنجاز من أهم المحطات في مسيرتي الرياضية، لأنه أثبت لي أن التعب والاجتهاد والانضباط في التدريب يمكن أن يصنعوا فرقاً كبيراً. ورغم الظروف الصعبة التي يمر بها قطاع غزة، بقي حلمي في كرة القدم حاضراً، وما زلت أطمح إلى تطوير نفسي أكثر والوصول إلى مستويات أعلى، وتمثيل أكاديميتي ومدينتي بأفضل صورة.\n\nختاماً، أعتبر أن تجربتي مع أكاديمية تشامبيونز كانت بداية حقيقية لطريقي في كرة القدم، وأتمنى أن أواصل العمل والاجتهاد حتى أحقق حلمي وأصبح لاعباً ناجحاً يرفع اسم فلسطين عالياً."
    },
    achievements: {
      en: [
        "First place in the 2008 age-group championship (2023)",
        "Defeated Deir Al-Balah United in the final"
      ],
      ar: [
        "المركز الأول في بطولة مواليد 2008 (2023)",
        "الفوز على اتحاد دير البلح في النهائي"
      ]
    },
    tags: ["football", "nuseirat", "youth", "champion"]
  },

  {
    id: "ss-football-abushaaban",
    name: {
      en: "Layan Abu Shaaban",
      ar: "لين أبو شعبان"
    },
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    image: "/assets/images/s-blob-v1-IMAGE-wA5xAwin9fo.jpeg",
    yearsAtAcademy: "2019 - Present",
    age: 16,
    nationality: {
      en: "Palestinian",
      ar: "فلسطينية"
    },
    level: {
      en: "National Team",
      ar: "منتخب وطني"
    },
    story: {
      en: "Basic information: Layan Abu Shaaban, 16 years old, from Gaza.\n\nThe beginning: I joined in 2019 without owning any experience. I challenged my young age and kept playing until I reached the first team.\n\nThe journey inside the academy: I learned everything inside the academy — skills, discipline, self-confidence, to love those around me, and that if I want something I am capable of doing it and the dream comes true. The moment I received a shield from the Palestinian Football Federation as the youngest outstanding player, with regular commitment.\n\nThe achievement: By God's grace, from Champions Academy I achieved everything — I won titles, moved up to the higher team, developed remarkably, and was honoured by the Palestinian Football Federation as its youngest player, and that was a special moment.\n\nCurrent status: A key starter, and — God willing — I aim to be the best player in the Arab world.",
      ar: "المعلومات الأساسية: لين أبو شعبان، 16 سنة، من غزة.\n\nالبداية: انضممت عام 2019 دون أن أملك أي خبرة. تحديت صغر سني وواصلت اللعب حتى وصلت إلى الفريق الأول.\n\nالرحلة داخل الأكاديمية: تعلمت كل شيء داخل الأكاديمية — مهارات، وانضباطاً، وثقة بالنفس، وأن أحب من حولي، وأنني إذا أردت شيئاً فأنا قادرة على تحقيقه وأن الحلم يتحقق. وكانت لحظة استلامي درعاً من الاتحاد الفلسطيني كأصغر لاعبة متميزة من أجمل اللحظات، مع التزام منتظم.\n\nالإنجاز: بفضل من الله، ومن أكاديمية تشامبيونز حققت كل شيء — حققت بطولات، وانتقلت إلى الفريق الأعلى، وتطورت تطوراً ملحوظاً، وتم تكريمي من الاتحاد الفلسطيني كأصغر لاعبة، وهذه كانت لحظة خاصة.\n\nالوضع الحالي: لاعبة أساسية، وأطمح أن أكون أفضل لاعبة في الوطن العربي بإذن الله."
    },
    quote: {
      en: "Champions is what made me strong and gave me confidence in myself; it changed my whole way of thinking and made me achieve my dream.",
      ar: "تشامبيونز هي التي قوّتني وأعطتني الثقة بنفسي، وغيّرت كل تفكيري وجعلتني أحقق حلمي."
    },
    achievements: {
      en: [
        "Honoured by the Palestinian Football Federation as its youngest outstanding player",
        "Promoted to the first team despite her young age",
        "Won multiple titles with the academy"
      ],
      ar: [
        "تكريمها من الاتحاد الفلسطيني كأصغر لاعبة متميزة",
        "الترقّي إلى الفريق الأول رغم صغر سنها",
        "تحقيق عدة بطولات مع الأكاديمية"
      ]
    },
    tags: ["football", "national-team", "female-athlete", "gaza"]
  },

  // ==========================================
  // BASKETBALL PLAYERS - لاعبات كرة السلة
  // ==========================================
  {
    id: "ss-basketball-bisiso",
    name: {
      en: "Sara Bisiso",
      ar: "سارة بسيسو"
    },
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    image: "/assets/images/s-blob-v1-IMAGE-GrqwWS_efRQ.jpeg",
    currentClub: {
      en: "University in California, USA",
      ar: "جامعة في كاليفورنيا، الولايات المتحدة"
    },
    position: {
      en: "Student Athlete & Leader",
      ar: "طالبة رياضية وقائدة"
    },
    yearsAtAcademy: "2019 - 2020",
    nationality: {
      en: "Palestinian",
      ar: "فلسطينية"
    },
    level: {
      en: "Semi-Professional",
      ar: "شبه محترف"
    },
    story: {
      en: "Name: Sara Bisiso. City: Gaza.\n\nI joined Champions Academy in the 2019/2020 season. Before joining, my level was intermediate; I had the basics of the game, but I needed to develop my technical and physical skills and gain more experience.\n\nDuring my journey at the academy, I witnessed great growth on both the sporting and personal levels. Among the most important developments I achieved was an increase in my self-confidence on the court, and a marked improvement in my technical and tactical skills. I also learned the importance of discipline, commitment, and teamwork, and became more able to take on responsibility and to deal with challenges and pressure. The academy's impact was not limited to developing me as a player; it also contributed to building my character and my leadership skills.\n\nOne of the most important achievements I gained was confidence in my skills and abilities as a player. I also developed my ability to work as a team and to build relationships with my teammates, both on and off the court, and became more able to cooperate, communicate, and work within a team to achieve shared goals. One fruit of this development was my reaching a leadership position at my university later on — something I consider an extension of the values and skills I gained during my years at the academy.\n\nI currently study Biology in the state of California in the United States of America. I also represent students in the university's student government, where I hold the position of Vice President for Academic Affairs, through which I strive to serve students, defend their academic interests, and contribute to developing their educational experience.\n\nThe skills and values I gained during my journey with Captain Hassan and the Champions team will stay with me for the rest of my life. I learned honesty, effective communication, and teamwork — the very skills that helped me reach a leadership position at my university today. I can say with all pride that the beginning of this journey was at Champions Academy, and it will remain an essential part of my success story no matter what achievements I reach in the future.",
      ar: "الاسم: سارة بسيسو. المدينة: غزة.\n\nانضممت إلى أكاديمية تشامبيونز في موسم 2019/2020. قبل الانضمام كان مستواي متوسطًا، وكنت أمتلك أساسيات اللعبة، لكنني كنت بحاجة إلى تطوير مهاراتي الفنية والبدنية واكتساب المزيد من الخبرة.\n\nخلال رحلتي في الأكاديمية، شهدت تطورًا كبيرًا على المستويين الرياضي والشخصي. من أهم التطورات التي حققتها زيادة ثقتي بنفسي داخل الملعب، وتحسن مهاراتي الفنية والتكتيكية بشكل ملحوظ. كما تعلمت أهمية الانضباط والالتزام والعمل الجماعي، وأصبحت أكثر قدرة على تحمل المسؤولية والتعامل مع التحديات والضغوط. ولم يقتصر تأثير الأكاديمية على تطويري كلاعبة فقط، بل ساهمت أيضًا في بناء شخصيتي ومهاراتي القيادية.\n\nمن أهم الإنجازات التي حققتها هو اكتساب الثقة بمهاراتي وقدراتي كلاعبة. كما طورت قدرتي على العمل الجماعي وبناء العلاقات مع فريقي، سواء داخل الملعب أو خارجه، وأصبحت أكثر قدرة على التعاون والتواصل والعمل ضمن فريق لتحقيق الأهداف المشتركة. وكان من ثمار هذا التطور وصولي لاحقًا إلى منصب قيادي في جامعتي، وهو ما أعتبره امتدادًا للقيم والمهارات التي اكتسبتها خلال سنواتي في الأكاديمية.\n\nأدرس حاليًا تخصص الأحياء في ولاية كاليفورنيا بالولايات المتحدة الأمريكية. كما أمثل الطلبة في الحكومة الطلابية بالجامعة، حيث أشغل منصب نائب رئيس الشؤون الأكاديمية، وأسعى من خلاله إلى خدمة الطلاب والدفاع عن مصالحهم الأكاديمية والمساهمة في تطوير تجربتهم التعليمية.\n\nالمهارات والقيم التي اكتسبتها خلال رحلتي مع الكابتن حسن وفريق تشامبيونز سترافقني طوال حياتي. فقد تعلمت الصدق، والتواصل الفعّال، والعمل الجماعي، وهي المهارات التي ساعدتني على الوصول إلى موقع قيادي في جامعتي اليوم. أستطيع أن أقول بكل فخر إن بداية هذه الرحلة كانت من أكاديمية تشامبيونز، وستبقى جزءًا أساسيًا من قصة نجاحي مهما كانت الإنجازات التي أحققها في المستقبل."
    },
    quote: {
      en: "Success is not a gift to be handed to you; it is the fruit of effort, hard work, and determination.",
      ar: "النجاح ليس هدية تُمنح، بل ثمرة جهدٍ وعملٍ وإصرار."
    },
    achievements: {
      en: [
        "Studying Biology at a university in California",
        "Vice President for Academic Affairs in student government",
        "Developed strong leadership skills through the academy"
      ],
      ar: [
        "تدرس الأحياء في جامعة بكاليفورنيا",
        "نائب رئيس الشؤون الأكاديمية في مجلس الطلبة",
        "اكتسبت مهارات قيادية قوية عبر الأكاديمية"
      ]
    },
    tags: ["basketball", "female-athlete", "leadership", "gaza"]
  },

  {
    id: "ss-basketball-abualnaja",
    name: {
      en: "Basma Abu Al-Naja",
      ar: "بسمة أبو النجا"
    },
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    image: "/assets/images/s-blob-v1-IMAGE-IYhvf5Sdg_U.jpeg",
    currentClub: {
      en: "University Basketball Team",
      ar: "فريق كرة السلة الجامعي"
    },
    yearsAtAcademy: "2020 - Present",
    nationality: {
      en: "Palestinian",
      ar: "فلسطينية"
    },
    level: {
      en: "Semi-Professional",
      ar: "شبه محترف"
    },
    story: {
      en: "Basic information: Name: Basma Abu Al-Naja. City: Gaza – Tal Al-Hawa.\n\nThe beginning: I joined Champions Academy for girls' basketball in 2020. At that time my level was weak, and one of the biggest challenges I faced was the difficulty of commitment and focus — but with time and training I was able to develop and improve.\n\nThe journey inside the academy: I learned basketball skills, sportsmanship, teamwork, cooperation, commitment, and self-confidence from the academy. A moment I still remember was during a match against another team, when the score was close and we were losing by a small margin. We really wanted to win, and I felt then that we had to support one another on the court so we could keep giving everything we had. So I encouraged the girls and communicated with them during play, and this helped us keep our enthusiasm and finish the match with a strong spirit.\n\nThe achievement: One of the most important things I took away from the academy was the clear development in my basketball skills, in addition to a wonderful experience I benefited from greatly.\n\nCurrent status: I have now joined the girls' basketball team at the university where I study, and I am still at the start of the path. My ambition now is to maintain my skills and ability to play, and to participate more in university activities and matches.\n\nMessage to the academy: Many thanks to Champions Club, and especially to Captain Hassan Ismail, who had a great role in building my sporting character and developing the skills and qualities I gained through this experience. I thank him for his constant support and encouragement, for his care for the team, and for his keenness to see us develop and become better continuously.",
      ar: "المعلومات الأساسية: الاسم: بسمة أبو النجا. المدينة: غزة – تل الهوى.\n\nالبداية: انضممت لأكاديمية تشامبيونز لكرة السلة للبنات سنة 2020. وقتها كان مستواي ضعيفًا، ومن أكثر التحديات التي واجهتني صعوبة الالتزام والتركيز، لكن مع الوقت والتدريب قدرت أتطور وأتحسن.\n\nالرحلة داخل الأكاديمية: تعلمت من الأكاديمية مهارات كرة السلة، والروح الرياضية، والعمل الجماعي، والتعاون، والالتزام، والثقة بالنفس. موقف بتذكره كان خلال مباراة ضد فريق تانٍ، وكانت النتيجة متقاربة وكنا خسرانين بفارق بسيط. كنا كثير معنيين نفوز، وحسيت وقتها إنه لازم ندعم بعض داخل الملعب عشان نكمل نعطي كل اللي عندنا. فكنت أشجع البنات وأتواصل معهم خلال اللعب، وهذا الشي ساعدنا نحافظ على حماسنا ونكمل المباراة بروح قوية.\n\nالإنجاز: من أهم الأشياء اللي طلعت فيها من الأكاديمية التطور الواضح في مهاراتي بكرة السلة، بالإضافة لتجربة جميلة استفدت منها كثير.\n\nالوضع الحالي: حاليًا انضممت لفريق كرة السلة للبنات في الجامعة اللي بدرس فيها، ولسا في بداية المشوار. وطموحي حاليًا إني أحافظ على مهاراتي وقدرتي في اللعب وأشارك أكثر بالأنشطة والمباريات الجامعية.\n\nرسالة للأكاديمية: جزيل الشكر لنادي تشامبيونز، وخصوصًا الكابتن حسن إسماعيل، اللي كان إله دور كبير في بناء شخصيتي الرياضية وتطوير المهارات والصفات اللي اكتسبتها خلال هذه التجربة. بشكره على دعمه وتشجيعه الدائم، وعلى اهتمامه بالفريق وحرصه إنه نشوف حالنا بنتطور ونصير أحسن باستمرار."
    },
    quote: {
      en: "Focus on the work and the steps, not just the goal. If the steps are going right, the goal will come. And take things one step at a time.",
      ar: "ركز على الشغل والخطوات، مش بس على الهدف. إذا الخطوات ماشية صح، الهدف رح يجي. وخذ الأمور خطوة بخطوة."
    },
    achievements: {
      en: [
        "Plays for her university basketball team",
        "Progressed from beginner to skilled player",
        "Built commitment, teamwork, and confidence at the academy"
      ],
      ar: [
        "تلعب في فريق كرة السلة بجامعتها",
        "تطورت من مستوى المبتدئين إلى لاعبة ماهرة",
        "بنت الالتزام والعمل الجماعي والثقة في الأكاديمية"
      ]
    },
    tags: ["basketball", "female-athlete", "gaza", "university"]
  },

  {
    id: "ss-basketball-abusamara",
    name: {
      en: "Suhar Abu Samara",
      ar: "سحر أبو سمرة"
    },
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    image: "/assets/images/s-blob-v1-IMAGE-IuZb2OMwJt0.jpeg",
    yearsAtAcademy: "2019 - Present",
    nationality: {
      en: "Palestinian",
      ar: "فلسطينية"
    },
    level: {
      en: "Semi-Professional",
      ar: "شبه محترف"
    },
    story: {
      en: "Suhar Abu Samara – Gaza.\n\nI joined the academy in 2019. My level was weak and needed a lot of development.\n\nDuring the years I trained at the academy, and with the continuous support of the coach, I was able to develop my level remarkably, learned many skills, and improved my performance in the game. Beyond developing my sporting skills, I also learned the value of commitment and discipline, and this was reflected positively on many aspects of my life. This experience also greatly strengthened my confidence in myself, on and off the court.\n\nOf course, the most important achievement was the noticeable development in play and in our relationship with the team and the coach. When we were honoured as 'player of the month,' it really motivated us to become better at playing and to develop ourselves even more.\n\nI was a key starter for the team, and — God willing — we will return to play again with the team and reach further places.\n\nChampions was one of the most beautiful experiences of my life and gave me the chance to change myself for the better.",
      ar: "سحر أبو سمرة – غزة.\n\nانضممت للأكاديمية سنة 2019، وكان مستواي ضعيفًا ومحتاجًا الكثير من التطوير.\n\nخلال السنين التي تدربت فيها بالأكاديمية، ومع الدعم المستمر من الكابتن، قدرت أطور مستواي بشكل ملحوظ، وتعلمت مهارات كثيرة، وتحسّن أدائي باللعبة. غير التطور بالمهارات الرياضية، تعلمت كمان قيمة الالتزام والانضباط، وهذا انعكس بشكل إيجابي على جوانب كثيرة من حياتي. كمان هذه التجربة عززت كثيرًا ثقتي بنفسي جوا وبرا الملعب.\n\nأكيد أهم إنجاز كان التطور الملحوظ باللعب وعلاقتنا بالفريق والكابتن، ولما كنا نتكرم نجم الشهر كان كثير بحفزنا إننا نصير أحسن باللعب ونطور من حالنا كمان وكمان.\n\nكنت لاعبة أساسية بالفريق، وإن شاء الله نرجع نلعب تاني مع الفريق ونوصل أماكن أبعد.\n\nتشامبيونز كان من أحلى التجارب بحياتي، وأعطاني فرصة أغير من حالي للأحسن."
    },
    quote: {
      en: "Champions was one of the most beautiful experiences of my life and gave me the chance to change myself for the better.",
      ar: "تشامبيونز كان من أحلى التجارب بحياتي، وأعطاني فرصة أغير من حالي للأحسن."
    },
    achievements: {
      en: [
        "Became a key starter for the team",
        "Progressed remarkably with her coach's support",
        "Learned commitment and discipline"
      ],
      ar: [
        "أصبحت لاعبة أساسية في الفريق",
        "تطورت بشكل ملحوظ بدعم الكابتن",
        "تعلمت الالتزام والانضباط"
      ]
    },
    tags: ["basketball", "female-athlete", "gaza"]
  },

  {
    id: "ss-basketball-sallouha",
    name: {
      en: "Salma Sallouha",
      ar: "سلمى صلوحة"
    },
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    image: "/assets/images/s-blob-v1-IMAGE-yvd-jbp4d9U.jpeg",
    yearsAtAcademy: "2019 - Present",
    nationality: {
      en: "Palestinian",
      ar: "فلسطينية"
    },
    level: {
      en: "Semi-Professional",
      ar: "شبه محترف"
    },
    story: {
      en: "Salma Sallouha – Gaza.\n\nWhen I started at the club my level was weak. I learned to be committed in everything, and I also developed a great deal in my play, and strong bonds formed within the team. We grew together through internal and external tournaments. I was a key starter for the team, and — God willing — we will return to play together again.\n\nChampions Club gave me the chance to grow myself in the game and to build relationships I would not have found anywhere else.",
      ar: "سلمى صلوحة – غزة.\n\nلما بدأت بالنادي كان مستوايَ ضعيفًا. تعلمت أن أكون ملتزمة بكل شيء، وكمان تطورت باللعب كثيرًا، وصار في علاقة قوية بالفريق. تطورنا مع بعض ببطولات داخلية وخارجية. كنت لاعبة أساسية بالفريق، وإن شاء الله نرجع نلعب تاني مع بعض.\n\nنادي تشامبيونز أعطاني فرصة أن أكبّر حالي باللعبة وأعمل علاقات ما كنت لألاقيها بمكان تانٍ."
    },
    quote: {
      en: "Champions Club gave me the chance to grow myself in the game and to build relationships I would not have found anywhere else.",
      ar: "نادي تشامبيونز أعطاني فرصة أن أكبّر حالي باللعبة وأعمل علاقات ما كنت لألاقيها بمكان تانٍ."
    },
    achievements: {
      en: [
        "Became a key starter for the team",
        "Built strong bonds with her teammates",
        "Developed steadily from a beginner level"
      ],
      ar: [
        "أصبحت لاعبة أساسية في الفريق",
        "بنت علاقات قوية مع زميلاتها",
        "تطورت تدريجياً من مستوى المبتدئين"
      ]
    },
    tags: ["basketball", "female-athlete", "gaza"]
  }
];

/**
 * Utility Functions
 */

// Get stories by sport
export const getStoriesBySport = (sport: string): SuccessStory[] => {
  return successStories.filter(story =>
    story.sport.en.toLowerCase().includes(sport.toLowerCase())
  );
};

// Get featured stories
export const getFeaturedStories = (): SuccessStory[] => {
  return successStories.filter(story => story.featured);
};

// Get stories by level
export const getStoriesByLevel = (level: string): SuccessStory[] => {
  return successStories.filter(story =>
    story.level.en.toLowerCase() === level.toLowerCase()
  );
};

// Get stories by tag
export const getStoriesByTag = (tag: string): SuccessStory[] => {
  return successStories.filter(story =>
    story.tags?.includes(tag.toLowerCase())
  );
};

// Get total achievements count
export const getTotalAchievements = (): number => {
  return successStories.reduce((total, story) => {
    return total + (story.achievements?.en.length || 0);
  }, 0);
};

/**
 * Statistics
 */
export const successStoriesStats = {
  total: successStories.length,
  byLevel: {
    professional: successStories.filter(s => s.level.en === 'Professional').length,
    semiProfessional: successStories.filter(s => s.level.en === 'Semi-Professional').length,
    nationalTeam: successStories.filter(s => s.level.en === 'National Team').length,
    international: successStories.filter(s => s.level.en === 'International').length,
  },
  bySport: successStories.reduce((acc, story) => {
    const sport = story.sport.en;
    acc[sport] = (acc[sport] || 0) + 1;
    return acc;
  }, {} as Record<string, number>),
  featured: successStories.filter(s => s.featured).length,
  totalAchievements: getTotalAchievements()
};
